import * as React from 'react';
import Account from '~/account/Account';
import { useDispatch } from 'react-redux';
import { DependencyList } from 'react';
import { getSupportedVideo } from '~/utils/video';
import { isEmptyObject, isInGameBrowser, isMobileOrTabletWindow } from '~/utils/utils';
import { appActions } from '~/Store/appSlice';
import { RootState, useAppSelector } from '~/Store';
import { UserGraphicsQualityPresetTypes } from '@wg/web2clientapi/browser/getClientSettings';

interface IHookEntryVideo {
    videoName?: string;
    fadeOutVideoInSeconds?: number;
    isViewAccountSpecific?: boolean;
    videoUrlsMap?: Partial<IVideo>;
    onFinishCallback?: DefaultFunction;
    isAllowedVideoForMobile?: boolean;
    categoryName?: string;
}

interface EntryVideoStatus {
    isNeedToPlay: boolean;
    isFinished: boolean;
}

const useEntryVideo = (
    { videoName, isViewAccountSpecific, videoUrlsMap, fadeOutVideoInSeconds, onFinishCallback, isAllowedVideoForMobile, categoryName }: IHookEntryVideo,
    deps: DependencyList = [],
) => {
    const dispatch = useDispatch();
    const isVisibleInfoScreen = useAppSelector((state: RootState) => state.app.infoScreen?.isVisible);
    const clientSettings = useAppSelector((state) => state.account.clientSettings);
    const [videoStatus, setVideoStatus] = React.useState<EntryVideoStatus>({
        isNeedToPlay: false,
        isFinished: false,
    });

    React.useLayoutEffect(() => {
        if (isVisibleInfoScreen) {
            return;
        }

        let _videoName = videoName;
        const videoUrl = getSupportedVideo(videoUrlsMap);
        if (!_videoName && videoUrl) {
            _videoName = (categoryName ? `${categoryName}_` : '') + videoUrl;
        }

        let isNeedToPlay = videoUrl && !Account.isViewedVideo(_videoName, isViewAccountSpecific);
        if (isNeedToPlay && isMobileOrTabletWindow) {
            isNeedToPlay = isAllowedVideoForMobile;
        }

        if (isNeedToPlay && isInGameBrowser) {
            isNeedToPlay = !isEmptyObject(clientSettings) && clientSettings.preset !== UserGraphicsQualityPresetTypes.LOW;
        }

        setVideoStatus({ isNeedToPlay, isFinished: false });

        if (isNeedToPlay) {
            dispatch(
                appActions.startVideo({
                    name: _videoName,
                    url: videoUrl,
                    onFinish: () => {
                        Account.markViewedVideo(_videoName, isViewAccountSpecific);
                        setVideoStatus({ isNeedToPlay: false, isFinished: true });
                        onFinishCallback?.();
                    },
                    fadeOutVideoInSeconds: fadeOutVideoInSeconds || 0,
                }),
            );
        }
    }, [isVisibleInfoScreen, videoUrlsMap, clientSettings, ...deps]);

    return {
        isNeedToPlay: videoStatus.isNeedToPlay,
        isFinished: videoStatus.isFinished,
    };
};

export default useEntryVideo;
